/* ============================================
   Artemis Theme — Designer Session
   ============================================
   Cross-page localStorage session for the
   apparel designer. Used by apparel PDPs (Add
   to Designer) and the /pages/designer page.

   Holds: a logo library (pageLogos[]) shared
   across the order, plus per-product entries
   that pick which library logo to use.
   ============================================ */

(function () {
  'use strict';

  // Loaded globally (theme.liquid) so the header designer badge works on every
  // page, and again per-page on apparel PDPs / the designer page. Bail on the
  // second load so we don't replace the live instance and drop its listeners.
  if (window.DesignerSession) return;

  var STORAGE_KEY = 'artemis_designer_session_v2';
  var listeners = [];

  function uuid() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return 'l-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
  }

  function readAll() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { entries: [], pageLogos: [] };
      var parsed = JSON.parse(raw);
      return {
        entries: Array.isArray(parsed.entries) ? parsed.entries : [],
        pageLogos: Array.isArray(parsed.pageLogos) ? parsed.pageLogos : [],
      };
    } catch (err) { /* ignore */ }
    return { entries: [], pageLogos: [] };
  }

  function writeAll(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        v: 2,
        entries: state.entries,
        pageLogos: state.pageLogos,
      }));
    } catch (err) { /* ignore */ }
    emit('change', state);
  }

  function read() {
    return readAll().entries;
  }

  function readPageLogos() {
    return readAll().pageLogos;
  }

  function emit(event, payload) {
    listeners.forEach(function (l) {
      if (l.event === event) {
        try { l.handler(payload); } catch (err) { /* ignore */ }
      }
    });
  }

  window.addEventListener('storage', function (e) {
    if (e.key !== STORAGE_KEY) return;
    var state = readAll();
    emit('change', state);
    emit('pageLogos', state.pageLogos);
  });

  /* ---- Entries ---- */
  function add(input) {
    var state = readAll();
    var entry = {
      id: uuid(),
      productHandle: input.productHandle,
      productId: input.productId,
      color: input.color || '',
      productSnapshot: input.productSnapshot || null,
      designs: input.designs || null,
      sizesQty: input.sizesQty || {},
    };
    state.entries.push(entry);
    writeAll(state);
    return entry;
  }

  function update(entryId, patch) {
    var state = readAll();
    var changed = false;
    for (var i = 0; i < state.entries.length; i++) {
      if (state.entries[i].id === entryId) {
        Object.keys(patch).forEach(function (k) { state.entries[i][k] = patch[k]; });
        changed = true;
        break;
      }
    }
    if (changed) writeAll(state);
  }

  function remove(entryId) {
    var state = readAll();
    state.entries = state.entries.filter(function (e) { return e.id !== entryId; });
    writeAll(state);
  }

  function clear() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (err) { /* ignore */ }
    emit('change', { entries: [], pageLogos: [] });
    emit('pageLogos', []);
  }

  /* ---- Page Logos (library) ---- */
  function addPageLogo(logo) {
    var state = readAll();
    var entry = {
      id: uuid(),
      url: logo.url,
      previewUrl: logo.previewUrl || logo.url,
      key: logo.key || '',
      name: logo.name || 'logo',
      contentType: logo.contentType || '',
      width: typeof logo.width === 'number' ? logo.width : null,
      height: typeof logo.height === 'number' ? logo.height : null,
      isVector: typeof logo.isVector === 'boolean' ? logo.isVector : null,
      colorCount: typeof logo.colorCount === 'number' ? logo.colorCount : null,
    };
    state.pageLogos.push(entry);
    writeAll(state);
    emit('pageLogos', state.pageLogos);
    return entry;
  }

  function removePageLogo(logoId) {
    var state = readAll();
    state.pageLogos = state.pageLogos.filter(function (l) { return l.id !== logoId; });
    writeAll(state);
    emit('pageLogos', state.pageLogos);
  }

  function renamePageLogo(logoId, name) {
    var state = readAll();
    var changed = false;
    state.pageLogos.forEach(function (l) {
      if (l.id === logoId && name && l.name !== name) {
        l.name = name;
        changed = true;
      }
    });
    if (changed) {
      writeAll(state);
      emit('pageLogos', state.pageLogos);
    }
  }

  function getPageLogo(logoId) {
    if (!logoId) return null;
    var logos = readPageLogos();
    for (var i = 0; i < logos.length; i++) {
      if (logos[i].id === logoId) return logos[i];
    }
    return null;
  }

  function clearPageLogos() {
    var state = readAll();
    state.pageLogos = [];
    writeAll(state);
    emit('pageLogos', []);
  }

  // Bulk-replace the whole session — used when resuming a saved design board.
  // Emits 'replace' (a dedicated event designer.js listens to for a single
  // full re-render; it must NOT listen to the noisy 'change' event).
  function replaceAll(state) {
    var next = {
      entries: (state && Array.isArray(state.entries)) ? state.entries : [],
      pageLogos: (state && Array.isArray(state.pageLogos)) ? state.pageLogos : [],
    };
    writeAll(next);                    // persists + emits 'change'
    emit('pageLogos', next.pageLogos); // refresh the logo library UI
    emit('replace', next);             // designer.js → renderAllEntries()
  }

  function on(event, handler) {
    listeners.push({ event: event, handler: handler });
  }

  function off(event, handler) {
    listeners = listeners.filter(function (l) { return !(l.event === event && l.handler === handler); });
  }

  window.DesignerSession = {
    list: read,
    add: add,
    update: update,
    remove: remove,
    clear: clear,
    count: function () { return read().length; },
    readAll: readAll,
    replaceAll: replaceAll,
    getPageLogos: readPageLogos,
    getPageLogo: getPageLogo,
    addPageLogo: addPageLogo,
    removePageLogo: removePageLogo,
    renamePageLogo: renamePageLogo,
    clearPageLogos: clearPageLogos,
    on: on,
    off: off,
  };
})();
